import { useFundingSourcePermissions } from '@melio/ap-domain';
import {
  getFundingSourceIconOrBrandSymbol,
  useFundingSourceCardDisplayText,
  useFundingSourceCardHelperText,
} from '@melio/ap-widgets';
import { Badge, Group, Icon, Text } from '@melio/penny';
import { FundingSource } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import React from 'react';

export const FundingSourceCard = ({
  fundingSource,
  isSelectedOption,
  isSelectedValue,
}: {
  fundingSource: FundingSource;
  isSelectedOption: boolean;
  isSelectedValue: boolean;
}) => {
  const { formatMessage } = useMelioIntl();
  const { canUpdate } = useFundingSourcePermissions({ fundingSource });
  const { getDisplayText } = useFundingSourceCardDisplayText();
  const { getHelperTextProps } = useFundingSourceCardHelperText();

  const icon = getFundingSourceIconOrBrandSymbol(fundingSource);
  const label = getDisplayText(fundingSource);
  const description = getHelperTextProps({
    fundingSource,
    //TODO: Implement micro deposits modal
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onVerify: () => {},
    canUpdateFundingSource: canUpdate,
  })?.label;

  return (
    <Group
      data-testid={`funding-source-card-${fundingSource.id}`}
      variant="horizontal"
      width="full"
      alignItems="center"
      justifyContent="space-between"
    >
      <Group variant="horizontal" width="full" alignItems="center">
        {icon}
        <Group variant="vertical" alignItems="flex-start" spacing="xxxs">
          <Group alignItems="center" spacing="xxs">
            <Text
              data-testid={`funding-source-card-${fundingSource.id}-label`}
              textStyle={isSelectedValue ? 'body2Semi' : 'body3'}
            >
              {label}
            </Text>

            {fundingSource.isDefault && (
              <Badge
                type="primary"
                status="neutral"
                aria-label="default-funding-source"
                data-testid="default-funding-source-badge"
                label={formatMessage('activities.paymentFlow.form.content.fundingSource.default')}
              />
            )}
          </Group>
          <Text
            data-testid={`funding-source-card-${fundingSource.id}-description`}
            textStyle="body4"
            color="neutral.darker"
          >
            {description}
          </Text>
        </Group>
      </Group>
      {isSelectedOption && (
        <Icon
          data-testid={`funding-source-card-${fundingSource.id}-selected`}
          type="checked"
          size="small"
          color="brand"
        />
      )}
    </Group>
  );
};
